import DropdownSearch from './_DropdownSearch';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { LinkProps, ImageProps, MetaProps } from '@repo/ui/src/types';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import { classNames } from '@repo/ui/src/utils/classNames';
import LinkGroup, { LinkGroupProps, LinkWithIconProps } from './_LinkGroup';
import LinkList, { LinkListProps } from './_LinkList';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import ProductList from './_ProductList';
import dynamic from 'next/dynamic';
import NavigationPromo from './_NavigationPromo';
import { navigationClicked } from '@repo/ui/src/services/analytics';
const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface NavDropdownProps {
  _meta: MetaProps;
  title?: string;
  description?: string;
  primaryHeader?: string;
  secondaryHeader?: string;
  navigationPromo?: {
    links: Array<LinkProps>;
    title: string;
    image?: ImageProps;
  };
  centerContent?: Array<LinkGroupProps | LinkListProps>;
  rightContent: {
    title: string;
    links: Array<LinkProps>;
    socialLinks?: {
      title?: string;
      links?: Array<LinkWithIconProps>;
    };
    search: {
      searchBy: string;
      searchPlaceholder: string;
    };
  };
  setActiveDropdown?: StateSetterFunction<NavDropdownProps | null>;
  showMobileHeader?: boolean;
}

interface Props extends NavDropdownProps {
  searchLink?: LinkProps;
  SocialLinks?: React.ComponentType<{ socialLinks: any }>;
}
const componentTypes = {
  'https://traeger.com/v2/linkGroup.json': LinkGroup,
  'https://traeger.com/v2/linkList.json': LinkList,
  'https://traeger.com/v2/productList.json': ProductList,
};

const NavDropdown = ({
  SocialLinks,
  centerContent,
  rightContent,
  description,
  title,
  searchLink,
  navigationPromo,
  setActiveDropdown,
  primaryHeader,
  secondaryHeader,
  showMobileHeader = false,
}: Props) => {
  const rightLinks = rightContent?.links?.filter(
    (item) => !!item?.displayName && item?.link && item.asButton === false,
  );
  const buttonItems = rightContent?.links?.filter((link) => link.asButton === true);
  const hasButton = !!buttonItems?.length;

  return (
    <>
      {showMobileHeader && (
        <div className="sm:tw-hidden tw-py-2 tw-relative tw-text-black tw-border-b tw-border-b-warm-200 tw-mb-5">
          <button
            className="tw-absolute tw-left-0 tw-p-3 tw-flex"
            aria-label="back previous navigation menu"
            onClick={() => setActiveDropdown?.(null)}
          >
            <ChevronIcon direction="left" className="!tw-w-6 !tw-h-6" />
          </button>
          <h2 className="tra-text-web-heading-4 tw-text-black tw-text-center tw-py-2">{title}</h2>
        </div>
      )}
      <div className="tw-relative tw-gap-y-10 tw-grid tw-content-start sm:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:tw-grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,2fr)] sm:tw-pt-10 md:tw-pb-15 sm:tw-px-6 md:tw-px-10 lg:tw-px-20 tw-w-full tw-h-full tw-gap-7 -tw-z-100 tw-max-w-[1680px] tw-pb-6 sm:tw-max-h-[calc(85vh-var(--tra-nav-height)-var(--dropdown-offset))] sm:tw-overflow-y-auto">
        <div className="tw-hidden lg:tw-flex tw-flex-col tw-max-w-[240px] tw-gap-6">
          <h4 className="tra-text-web-heading-4 tw-text-neutral-700">{title}</h4>
          {description && <Markdown bodySize="large">{description}</Markdown>}
        </div>
        <div className="tw-grid tw-gap-y-10">
          {navigationPromo?.title && (
            <NavigationPromo
              {...navigationPromo}
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          )}
          {centerContent?.map((props, i) => (
            <DynamicComponent
              key={i}
              {...props}
              _componentTypes={componentTypes}
              _field="_meta.schema"
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          ))}
        </div>
        <div className="tw-px-4 sm:tw-px-0">
          {rightContent?.search && (
            <div>
              <DropdownSearch {...rightContent.search} link={searchLink?.link} />
            </div>
          )}
          <div>
            {(!!rightLinks?.length || !!buttonItems?.length) && (
              <ul className="tw-px-0 tw-list-none">
                {rightLinks.map(
                  ({ link, displayName, isExternal, externalLink, anchorTag }, index) => (
                    <li key={index}>
                      <Button
                        key={displayName}
                        isExternal={isExternal}
                        externalLink={externalLink}
                        variant={ButtonVariant.knife}
                        link={link}
                        anchorTag={anchorTag}
                        as={Anchor}
                        className={classNames(
                          'tw-text-neutral-700 !tw-flex !tw-justify-between !tw-w-full tw-rounded-none !tra-text-web-sub-3 !tw-pb-4 !tw-pt-4 first:tw-pt-0',
                          (!rightContent?.socialLinks || hasButton) && 'last:tw-border-b-0',
                          index === 0 && !rightContent?.search && '!tw-pt-0',
                        )}
                        onClick={() =>
                          navigationClicked({
                            nav_type: 'Top Navigation',
                            clicked_text: displayName ?? '',
                            primary_header: primaryHeader ?? '',
                            secondary_header: secondaryHeader ?? '',
                            tertiary_header: displayName ?? '',
                          })
                        }
                      >
                        {displayName}
                      </Button>
                      {index !== rightLinks.length - 1 && (
                        <hr className="tw-border-warm-300 tw-border-b" />
                      )}
                    </li>
                  ),
                )}
                {buttonItems?.map(
                  ({ link, displayName, isExternal, externalLink, anchorTag }, index) => (
                    <li key={index + (rightLinks?.length ?? 0)}>
                      <Button
                        key={displayName}
                        isExternal={isExternal}
                        externalLink={externalLink}
                        variant={ButtonVariant.secondary}
                        link={link}
                        anchorTag={anchorTag}
                        as={Anchor}
                        full
                        size="small"
                        className={classNames('tw-text-neutral-700 tw-text-center !tw-w-fit')}
                        onClick={() =>
                          navigationClicked({
                            nav_type: 'Top Navigation',
                            clicked_text: displayName ?? '',
                            primary_header: primaryHeader ?? '',
                            secondary_header: secondaryHeader ?? '',
                            tertiary_header: displayName ?? '',
                          })
                        }
                      >
                        {displayName}
                      </Button>
                    </li>
                  ),
                )}
              </ul>
            )}
            {rightContent?.socialLinks && SocialLinks && <SocialLinks socialLinks={rightContent.socialLinks} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default NavDropdown;
